import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  ArchiveBoxIcon,
  ArrowDownOnSquareIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import Alert from "../Alert";
import ActionButton from "../ActionButton";
import StepAwaySave, { ConfigFileSaveError } from "./StepAwaySave";
import StartOverButton from "./StartOverButton";
import { useConfigFileSave } from "../../hooks/useConfigFileSave";
// tracking-01 docs/COOLDOWN-MANAGEMENT.md : the single-action sibling
// of ChallengePanel's per-challenge hook. Check Progress is per SESSION, so
// there is nothing to key on.
import { useCooldown, cooldownLabel } from "../../hooks/useMultiCooldown";
import { useCopyFeedback } from "../../hooks/useCopyFeedback";
import { downloadFile } from "../../utils/helpers";
import {
  sanitizeFilename,
  generateTimestamp,
} from "../../../shared/utils/fileHelpers";
import {
  ARCHIVE_MEMBERS,
  archiveFilename,
  archivePrefix,
  archiveReadme,
} from "../../issuance/certificateArchive";
import { downloadAsZip } from "../../utils/zipHelper";
import { exportConfigFile } from "../../issuance/configFile";
import {
  CONFIG_FILE_EXTENSION,
  CONFIG_FILE_MIME,
  // 2026-08-14 go-public review : the private key description
  // interpolates the shared lifetime constant, same single-sourcing as
  // the step-away line in StepAwaySave.jsx and the restore-failure
  // copy in src/client/pages/Issuance.jsx. It was the success Alert
  // until the operator removed that box on 2026-08-23 ; the constant
  // now has one consumer here rather than two, and still no
  // hand-written 48.
  SESSION_LIFETIME_HOURS,
  // 2026-08-14 go-public review : shared cooldown discriminator, same
  // single-literal contract as ChallengePanel's TRIGGER_COOLDOWN_CODE.
  FINALIZE_POLL_COOLDOWN_CODE,
  FINALIZE_POLL_COOLDOWN_MS,
} from "../../../shared/issuance/config";
import {
  apiFinalizeAndIssue,
  apiGetConfiguration,
  ApiError,
  LONG_CALL_TIMEOUT_MS,
  SHORT_CALL_TIMEOUT_MS,
  withTimeoutSignal,
} from "../../issuance/apiClient";

/**
 * Step 4 - Finalize the order, download the SSL Certificate, and
 * present every artifact to the customer.
 *
 * Post-pivot architecture : the server submits the Certificate Signing
 * Request (CSR) to Sectigo® once, then a single order-status check per
 * call. There is no automatic polling - consistent with Step 3's
 * manual model, where "Request Validation" (behind the per-domain
 * Validation Method selector since the 2026-07-04 presentation
 * redesign) triggers Sectigo® once and the read-only "Check
 * Validation Status" polls the result (runTrigger / runCheck in
 * ChallengePanel.jsx) :
 *   - On entry the component fires one finalize call automatically.
 *   - If Sectigo® has signed, the artifacts render.
 *   - If the Certificate Authority is still signing, a "Check Progress"
 *     button appears ; the
 *     customer presses it to check again (the finalize POST is not
 *     repeated - the server only re-reads the order status).
 *   - On a hard failure, a "Retry" button re-runs the flow.
 *
 * The Configuration File download includes the order URL, so it doubles
 * as a resume token even at this step.
 *
 * Props :
 *   sessionId       - server session id
 *   primaryDomain   - used in download filenames
 *   domains         - array of all domains
 *   certKeyTypeId   - selected SSL Certificate key type id
 *   serverId        - ACME service id (for Configuration File)
 *   serverUrl       - ACME service URL (for Configuration File)
 *   onSuccess       - invoked with the bundle once issuance completes
 *   onReset         - the page-level Start Over handler (handleReset
 *                     in src/client/pages/Issuance.jsx). Operator
 *                     directive 2026-07-05 : every section carries its
 *                     own centered action row. Step 4 is TERMINAL, so
 *                     there is no Continue here - in the complete
 *                     state Start Over is the PRIMARY action ("begin a
 *                     new SSL Certificate order"), and in the
 *                     processing / failed states it renders as the
 *                     outline escape hatch beside Check Progress /
 *                     Retry (the stalled-order exit the ChallengePanel
 *                     guidance points customers at).
 */
function Step4Download({
  sessionId,
  primaryDomain,
  domains,
  certKeyTypeId,
  serverId,
  serverUrl,
  onSuccess,
  onReset,
}) {
  // idle | working | processing | complete | failed
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  // The server's post-submit poll-cooldown message (a 429 with code
  // FINALIZE_POLL_COOLDOWN), rendered as an info notice on the
  // processing screen - never through the failed state (2026-08-14
  // go-public review ; see the runCheck catch).
  const [pollNotice, setPollNotice] = useState(null);
  /**
   * The Check Progress wait, counted down on the button itself.
   *
   * OPERATOR RULING, 2026-08-22, from his live run : "the buttons have
   * a timer on them to let the customer know ... and would perhaps
   * even prevent them from clicking the button at all". He raised it
   * against Step 3, and this is the same fault on Step 4 - a five
   * second window a customer meets by pressing into it. Not keyed :
   * the finalize poll is per SESSION, unlike Step 3's per-challenge
   * cooldowns, so there is one button and nothing to key on.
   */
  const pollCooldown = useCooldown(FINALIZE_POLL_COOLDOWN_MS);
  // Destructured so runCheck can depend on the two STABLE mutators
  // rather than on the whole object, whose readers change every tick.
  const {
    startCooldown: startPollCooldown,
    seedRemainingMs: seedPollCooldown,
  } = pollCooldown;
  // Committed-state mirrors, synced by the effect below : runCheck
  // captures the screen it STARTED from off these refs so the
  // poll-cooldown branch can restore it - a cooldown during Retry
  // must keep the failed screen (2026-08-14 go-public review). Mirror
  // refs rather than functional-setter capture because React runs
  // state-updater functions at render time, so a capture inside
  // setStatus((prev) => ...) can land AFTER the catch has already
  // read the ref (observed : the restore then painted the signing
  // screen anyway - the exact defect this exists to fix).
  const committedStatusRef = useRef("idle");
  const committedErrorRef = useRef(null);
  useEffect(() => {
    committedStatusRef.current = status;
  }, [status]);
  useEffect(() => {
    committedErrorRef.current = error;
  }, [error]);
  const [bundle, setBundle] = useState(null);
  // 2026-08-13 go-public cleanup (audit : "copy-feedback-drift") : the
  // hand-rolled copy state + timer is replaced by the shared
  // useCopyFeedback hook. Two fixes ride along : the generation guard
  // for the async clipboard gap (four Copy buttons are on screen at
  // once here, and a slow write could previously light "Copied!"
  // beside the WRONG artifact - including beside the Leaf while the
  // clipboard actually held the Private Key), and a rendered FAILED
  // state (the old render only tested === "copied", so a failed copy
  // of the Private Key showed no feedback at all).
  const { copyStatus, handleCopy } = useCopyFeedback();
  // The two-press guard itself now lives in StartOverButton, shared
  // with every other Start Over on the tool (operator ruling
  // 2026-08-17). What stays here is the DISARM : the download
  // handlers bump this counter, and any change to it stands the
  // button back down (2026-08-14 go-public review : an armed flag
  // surviving a download left the button one accidental click from
  // destroying the Private Key, the exact click the guard exists to
  // absorb ; a customer who armed it and then went off to download
  // has visibly changed intent).
  const [disarmToken, setDisarmToken] = useState(0);
  const disarmReset = useCallback(() => setDisarmToken((n) => n + 1), []);
  // Held at component level, NOT inside the processing branch : the
  // failure can arrive after that branch has stopped rendering (see
  // ConfigFileSaveError's header for the exact sequence).
  const configSave = useConfigFileSave(sessionId, primaryDomain);

  // The signing wait's step-away save disarms too (2026-08-17 review
  // round 2). Before the shared guard landed, the processing state's
  // Start Over was a plain single-press button, so there was nothing
  // to leave primed and this wiring did not exist ; adding the guard
  // created the shape round 1 had just fixed on Step 3. The
  // StepAwaySave line above that row offers the customer their
  // configuration file download for when they need to step away, so
  // following that advice must not leave them one press from
  // discarding the request it was meant to preserve.
  const saveThenDisarm = useCallback(() => {
    disarmReset();
    return configSave.save();
  }, [configSave, disarmReset]);

  // Guard against React StrictMode double-mount in development - we
  // want exactly one automatic finalize call per arrival on this step.
  // 2026-05-31 seventh-round code review I7 : ranRef must be keyed
  // on the sessionId, not on "this component instance". If the
  // parent ever re-passes a different sessionId without unmounting
  // (a flow we do not have today but could in a future refactor)
  // the captured value would be stale and runCheck would still
  // skip. ranForSessionRef stores the sessionId for which the
  // automatic call already ran ; the useEffect below compares
  // against the current sessionId prop and re-runs if it changed.
  const ranForSessionRef = useRef(null);

  // Mounted guard - pattern-sweep partner to the ones in ChallengePanel,
  // Step1Service, Step2Configure, and useConfigFileSave. runCheck (and the
  // ZIP/Configuration File download handlers below) await
  // multi-second server round-trips ; if the customer navigates away
  // mid-flight, setState on the resolved Promise should be skipped to
  // keep the pattern consistent across every async-setState site.
  // The copy-status timer that review I2 (2026-05-31) taught this
  // effect to cancel lives inside useCopyFeedback now, which owns its
  // own unmount cleanup.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * One finalize round-trip : submit the CSR (server does this only on
   * the first call) and read the order status once. Updates UI state
   * to complete / processing / failed.
   *
   * 2026-07-04 tools-wide overhaul (operator-approved), audit item
   * Tier 1 #4 : wrapped in useCallback (deps : sessionId, onSuccess -
   * the only props it closes over ; refs and setState functions are
   * stable by contract) so the automatic-finalize useEffect below can
   * list runCheck in its dependency array and satisfy
   * react-hooks/exhaustive-deps without an inline directive
   * (noInlineConfig forbids those). Behaviour is unchanged : the
   * ranForSessionRef guard still keys the automatic call on sessionId,
   * so a re-created runCheck (e.g. a new onSuccess identity) re-runs
   * the effect but the guard skips unless the sessionId itself changed.
   */
  const runCheck = useCallback(async () => {
    // The mirror refs (see their declaration) already hold the
    // COMMITTED status / error at the moment the customer pressed,
    // so freezing them here is a synchronous read - the useCallback
    // closure's own `status` / `error` would be stale, and a
    // functional-setter capture races the catch (React may run the
    // updater during a later render, after the catch already read it).
    const statusBeforeCheck = committedStatusRef.current;
    const errorBeforeCheck = committedErrorRef.current;
    setStatus("working");
    setError(null);
    // Each check supersedes a standing poll-cooldown notice : its own
    // outcome (still signing / complete / cooldown again) is fresher
    // information (2026-08-14 go-public review).
    setPollNotice(null);
    // 2026-08-13 go-public cleanup (audit : "functionality-wedge") : a
    // bounded AbortController on the long /finalize call. A hung
    // connection previously froze the "working" screen forever with
    // no control on it ; now it aborts after LONG_CALL_TIMEOUT_MS
    // (see its JSDoc in apiClient.js) and lands in the catch below as
    // a normal failed state, whose Retry / Start Over row is the
    // escape. The catch maps the AbortError's lowercase browser prose
    // to a branded retryable message.
    try {
      const result = await withTimeoutSignal(LONG_CALL_TIMEOUT_MS, (signal) =>
        apiFinalizeAndIssue(sessionId, { signal })
      );
      if (!mountedRef.current) return;
      // Starts on the ATTEMPT : the server's window opened when it took
      // the call, so an answer of "still signing" spent it exactly like
      // an answer of "complete".
      startPollCooldown();
      if (result && result.stillProcessing) {
        // The CA is still signing - wait for the customer to press
        // Check Progress.
        setStatus("processing");
        return;
      }
      // Guard every artifact the UI renders : leaf added PR #76
      // review second pass (a missing leaf previously produced an
      // empty "Your SSL Certificate" textarea while the rest of the
      // bundle rendered).
      if (
        !result ||
        !result.leaf ||
        !result.fullChain ||
        !result.certificatePrivateKey
      ) {
        throw new Error(
          "The server returned an empty SSL Certificate bundle. Please try again."
        );
      }
      setBundle(result);
      setStatus("complete");
      if (typeof onSuccess === "function") {
        try {
          onSuccess(result);
        } catch {
          // Page-level onSuccess should never throw, but swallow so the
          // UI stays in the completed state if it does.
        }
      }
    } catch (e) {
      if (!mountedRef.current) return;
      // The post-submit poll cooldown is INFORMATION, never failure
      // (2026-08-14 go-public review) : a second Check Progress inside
      // FINALIZE_POLL_COOLDOWN_MS on a healthy still-signing order
      // previously fell through this catch into the red terminal
      // "SSL Certificate Issuance Failed" banner. Recognised by the
      // machine-readable envelope code, the same contract
      // ChallengePanel applies to TRIGGER_COOLDOWN ; the customer
      // keeps the signing screen with the server's wait message as a
      // notice.
      if (
        e instanceof ApiError &&
        e.status === 429 &&
        e.body &&
        e.body.code === FINALIZE_POLL_COOLDOWN_CODE
      ) {
        setPollNotice(e.message);
        // The server's figure is authoritative, so the countdown on the
        // button and the wait in the notice cannot disagree.
        if (typeof e.body?.retryAfterMs === "number") {
          seedPollCooldown(e.body.retryAfterMs);
        }
        // RESTORE the pre-check screen (2026-08-14 go-public review) : a
        // cooldown answer carries no news about the order, so the
        // customer must land back where they pressed - a Retry
        // from the failed screen keeps its red banner (with the
        // cooldown notice beside it), never the healthy signing
        // screen. Every other origin (Check Progress on processing, the
        // automatic first call) correctly lands on "processing".
        if (statusBeforeCheck === "failed") {
          setError(errorBeforeCheck);
          setStatus("failed");
        } else {
          setStatus("processing");
        }
        return;
      }
      // ARM THE WINDOW ON AN AMBIGUOUS FAILURE TOO (round 4 of PR
      // #106). finalizeAndIssue records lastFinalizePollAt before it
      // calls the Certificate Authority, so a press that timed out or
      // came back 5xx has very likely spent the window even though it
      // returned nothing - leaving Retry pressable and refused.
      //
      // ARMED LOCALLY RATHER THAN READ BACK, unlike ChallengePanel,
      // and the difference is the size of the window. There is the
      // trigger's five minutes to be wrong about, so that one asks the
      // server ; here it is five seconds, and no read endpoint reports
      // lastFinalizePollAt, so a round trip to be certain about five
      // seconds would cost more than being wrong about them. If this
      // over-arms because the request never reached the server, the
      // customer waits five seconds they did not owe.
      startPollCooldown();
      setStatus("failed");
      // ApiError messages and server-mapped error.message strings
      // arrive as sentences from the server-side error envelope ; the
      // fallbacks we own are sentences too (2026-08-27).
      // AbortError is our own client-side timeout (the bounded
      // controller above) mapped to a branded retryable line.
      setError(
        e && e.name === "AbortError"
          ? "The SSL Certificate status check timed out. Retry to check again."
          : e instanceof ApiError
            ? e.message
            : e && e.message
              ? e.message
              : "Unknown error."
      );
    }
  }, [sessionId, onSuccess, startPollCooldown, seedPollCooldown]);

  // 2026-05-31 seventh-round code review I7 : key the automatic-
  // finalize trigger on sessionId rather than on the component's
  // first mount. If the parent ever re-passes a different
  // sessionId (e.g. customer goes back, starts a new order, and
  // returns to Step 4 without a full unmount) the new sessionId
  // must run a fresh automatic finalize call. The ref compares
  // against the prop ; the eslint-disable is dropped because the
  // dep array now correctly lists sessionId.
  // 2026-07-04 : the stray `eslint-disable-next-line
  // react-hooks/exhaustive-deps` that used to sit above the dep array
  // is removed - the I7 comment above already records that it was
  // dropped, and eslint.config.js sets `noInlineConfig: true` with
  // `reportUnusedDisableDirectives: "error"`, which makes any inline
  // directive a lint error the moment .jsx linting is enabled.
  // 2026-07-04 tools-wide overhaul (operator-approved), audit item
  // Tier 1 #4 : runCheck now appears in the dependency array (it is a
  // useCallback above, see the runCheck JSDoc), which is why this
  // effect sits AFTER the runCheck definition - a const is not
  // hoisted, so the effect must be declared later in the component
  // body to reference it.
  useEffect(() => {
    if (ranForSessionRef.current === sessionId) return;
    ranForSessionRef.current = sessionId;
    void runCheck();
  }, [sessionId, runCheck]);

  /**
   * ONE prefix for everything this issuance produces (2026-08-23,
   * operator : "the naming shouldn't differ too much"). The ZIP, each
   * artifact's own Download button and the archive's README all read
   * it, so a customer who saves the files one at a time ends up with
   * exactly the set the ZIP would have handed them - same domain, same
   * instant, tracking-01's `{identifier}-{YYYYMMDD-HHMMSS}` shape.
   *
   * Computed ONCE per mounted issuance rather than per file. The
   * previous helper stamped each filename as it was built, so a second
   * ticking over mid-archive produced members whose timestamps
   * disagreed with each other inside the same ZIP.
   *
   * A LAZY useState INITIALISER, NOT useMemo (PR #108 round 2). React
   * documents useMemo as a performance hint it may discard, and a
   * discarded cache here would read the clock again and split the
   * archive's name from the per-artifact names - the one invariant
   * this whole change rests on. React does not discard it today, so
   * this is not a live defect being fixed ; it is the difference
   * between relying on current behaviour and stating the guarantee.
   * The initialiser runs once per mount and the setter is deliberately
   * not taken : nothing may replace this value while the issuance is
   * on screen.
   */
  const [filePrefix] = useState(() =>
    archivePrefix(sanitizeFilename(primaryDomain), generateTimestamp())
  );
  const fileFor = (member) => archiveFilename(filePrefix, member);

  // In-flight flag for the issued screen's Download Configuration
  // button (operator ruling 2026-10-02) : while the request runs the
  // button is disabled and reads "Preparing Configuration File...",
  // the treatment useConfigFileSave gives the step-away link, so a
  // second press cannot start a parallel /configuration request.
  const [downloadingConfig, setDownloadingConfig] = useState(false);

  const handleDownloadConfigFile = async () => {
    if (!bundle) return;
    // Disarm the two-press reset guard : see the disarmToken comment
    // (2026-08-14 go-public review).
    disarmReset();
    // 2026-08-13 go-public cleanup (audit : "state-handling") : clear
    // any previous download failure before retrying, matching the save
    // in useConfigFileSave and runCheck in this file - the
    // old code left the red "Download Failed" Alert on screen after a
    // retry that had actually succeeded.
    setError(null);
    setDownloadingConfig(true);
    try {
      // 2026-05-31 seventh-round code review I3 : the issued bundle
      // (returned by POST /finalize, which is where Step 4 actually
      // obtains it - the former GET /certificate route was deleted in
      // the 2026-08-13 go-public cleanup as unreached surface) no
      // longer carries the External Account Binding (EAB)
      // credentials. Fetch the full Configuration File payload from
      // /configuration here, on demand, so EAB is only in browser
      // memory briefly when the customer actually presses the
      // Download Configuration button. This narrows
      // the cookie-disclosure blast radius : an attacker who steals
      // the cookie still cannot exfiltrate EAB unless they also make
      // this explicit /configuration request, and that request
      // stands out in access logs in a way that the automatic
      // finalize call does not.
      // Bounded fetch (2026-08-14 go-public review, the same wedge
      // family as the save in useConfigFileSave - the same request,
      // the same hazard) : this handler holds downloadingConfig true
      // across the await, so an unbounded hang would leave the button
      // disabled PERMANENTLY - on the one post-issuance resume
      // artifact.
      const data = await withTimeoutSignal(SHORT_CALL_TIMEOUT_MS, (signal) =>
        apiGetConfiguration(sessionId, { signal })
      );
      if (!data || !data.eab || !data.eab.kid || !data.eab.hmacKey) {
        throw new Error(
          "The server response is missing the External Account Binding (EAB) credentials. Please refresh the page and try again."
        );
      }
      // Merge the freshly-fetched Configuration File payload
      // with the issued-certificate artifacts we already have
      // in `bundle`. We deliberately prefer `data` for every
      // field it provides (it IS the canonical Configuration
      // File source-of-truth) ; the certificate.* block is the
      // only thing that comes from the bundle because
      // /configuration does not echo the issued certificate
      // back.
      const text = exportConfigFile({
        serverId: data.serverId || serverId,
        serverUrl: data.serverUrl || serverUrl,
        eab: data.eab,
        accountKeyPem: data.accountKeyPem,
        accountUrl: data.accountUrl,
        orderUrl: data.orderUrl,
        certKeyTypeId: data.certKeyTypeId || certKeyTypeId,
        certPrivateKeyPem: data.certPrivateKeyPem,
        domains: data.domains || domains,
        certificate: {
          leaf: bundle.leaf,
          intermediates: bundle.intermediates,
          fullChain: bundle.fullChain,
        },
      });
      // The SAME prefix the archive uses, not a second reading of the
      // clock. Everything this screen hands the customer belongs to
      // one issuance and should say so, and the old separate call
      // could land in a different second from the files beside it.
      const filename = filePrefix + "." + CONFIG_FILE_EXTENSION;
      downloadFile(text, filename, CONFIG_FILE_MIME);
    } catch (e) {
      // 2026-07-03 PR #76 sixth-pass review (Finding 4a) : guard the
      // setError with mountedRef, matching every other async handler in
      // this file (runCheck and handleDownloadZip both gate setState on
      // mountedRef.current). handleDownloadConfigFile awaits a
      // multi-second /configuration round-trip ; if the customer
      // navigates away mid-flight, setState on the unmounted component
      // is a no-op in React, but the guard is the file's convention.
      if (!mountedRef.current) return;
      // The mapping useConfigFileSave applies (operator ruling
      // 2026-10-02) : an ApiError's message is the server's own
      // complete sentence, so it is shown as it is. The prefix is for
      // failures raised in the browser, whose text does not say what
      // failed.
      setError(
        e && e.name === "AbortError"
          ? "The configuration file request timed out. Please try again."
          : e instanceof ApiError
            ? e.message
            : "The configuration file could not be built. " +
              (e.message || "Unknown error.")
      );
    } finally {
      if (mountedRef.current) {
        setDownloadingConfig(false);
      }
    }
  };

  const handleDownloadZip = async () => {
    if (!bundle) return;
    // Disarm the two-press reset guard : see the disarmToken comment
    // (2026-08-14 go-public review).
    disarmReset();
    // Same stale-failure reset as handleDownloadConfigFile above
    // (2026-08-13 go-public cleanup, audit : "state-handling").
    setError(null);
    // PR #76 review third pass : only add the intermediate chain entry
    // when there actually IS an intermediate, MIRRORING the artifact
    // card's `{bundle.intermediates && ...}` render gate. A root-only or
    // self-signed bundle can arrive with no intermediates ; adding the
    // entry unconditionally produced a chain.pem holding an empty string
    // inside the ZIP. The leaf, full chain, and Private Key entries stay
    // unconditional - runCheck's empty-bundle guard already rejects a
    // bundle missing any of those three.
    //
    // THE NAMES ARE tracking-01's DICTIONARY, not this tool's former
    // one. Until 2026-08-23 the intermediates-only file was called
    // `chain` and the leaf-plus-intermediates file was called
    // `fullchain`, each one slot more inclusive than tracking means by
    // the same word - so a customer who had read that system's README
    // took `fullchain` to carry the Root SSL Certificate. It never
    // did : RFC 8555 §7.4.2 says the Certificate Authority should not
    // send the trust anchor, so no file here can contain one.
    const files = [
      { filename: fileFor(ARCHIVE_MEMBERS.leaf), content: bundle.leaf },
      ...(bundle.intermediates
        ? [
            {
              filename: fileFor(ARCHIVE_MEMBERS.intermediates),
              content: bundle.intermediates,
            },
          ]
        : []),
      { filename: fileFor(ARCHIVE_MEMBERS.chain), content: bundle.fullChain },
      {
        filename: fileFor(ARCHIVE_MEMBERS.privateKey),
        content: bundle.certificatePrivateKey,
      },
      // Named README.txt exactly as tracking-01 names it, and last for
      // the same reason : it describes the files above it.
      { filename: "README.txt", content: archiveReadme(filePrefix) },
    ];
    const zipFilename = filePrefix + "-certificates.zip";
    try {
      await downloadAsZip(files, zipFilename);
    } catch (e) {
      // 2026-07-03 PR #76 sixth-pass review (Finding 4a) : same
      // mountedRef guard as handleDownloadConfigFile above.
      // downloadAsZip awaits async compression ; skip setState if the
      // customer unmounted mid-flight, matching this file's convention
      // (runCheck).
      if (!mountedRef.current) return;
      // 2026-05-31 seventh-round code review B3 : ZIP is not an
      // acronym (the format takes its name from Phil Katz's
      // PKZIP and is written ZIP only as a stylistic UPPERCASE,
      // not as initials). The previous code fabricated an
      // expansion "Zone Information Protocol (ZIP)" which
      // violates the never-guess rule on user-facing copy. Just
      // say ZIP.
      setError("The ZIP archive could not be created. " + e.message);
    }
  };

  // ONE construction of the poll-cooldown notice (2026-08-14
  // go-public review) : the identical info Alert renders on BOTH the
  // processing screen and the restored failed screen, and two
  // hand-written copies drift the first time the title or the close
  // behaviour changes. The server message carries the exact seconds
  // remaining, so it is surfaced verbatim.
  const pollNoticeAlert = pollNotice && (
    <Alert
      type="info"
      title="Status Just Checked"
      live
      onClose={() => setPollNotice(null)}
    >
      {pollNotice}
    </Alert>
  );

  /**
   * THE COMPLETION ANNOUNCEMENT, PERSISTENT AND INVISIBLE.
   *
   * The green Alert the operator removed on 2026-08-23 carried `live`
   * and was the only ARIA live region on the issued screen. Without it
   * a screen-reader user pressed Check Progress, heard "Signing Your SSL
   * Certificate", and then the whole subtree swapped in silence : the
   * page-level region in Issuance.jsx fires only on a currentStep
   * change, and this transition happens INSIDE Step 4. Nothing here
   * moves focus either.
   *
   * PERSISTENT, NOT CONDITIONALLY INSERTED (2026-08-23 review of PR
   * #107 round 8). My first attempt put the region inside the complete
   * branch, so it was created with its text already in it - and
   * ChallengePanel's own sr-only region carries the rule that forbids
   * exactly that : "live regions announce CONTENT CHANGES reliably only
   * when the region itself already exists in the accessibility tree".
   * A conditionally-inserted region reproduces the silence it was added
   * to fix. This one renders in every status, above the four branch
   * returns, and only its CONTENT changes.
   *
   * It sits OUTSIDE the branch roots rather than inside them, which
   * also keeps it clear of the complete branch's `space-y-6` : sr-only
   * is position:absolute but carries no [hidden], so as a first child
   * it would have pushed the whole issued screen down by one gap.
   *
   * sr-only, so "remove the entire green message box" holds in full -
   * there is nothing to see. Polite : completion is a progress update,
   * not an interrupt.
   */
  const completionAnnouncement = (
    <div role="status" aria-live="polite" className="sr-only">
      {status === "complete" && bundle
        ? "Your SSL Certificate has been issued and is ready to download."
        : ""}
    </div>
  );

  // ONE RETURN, so the live region above cannot be unmounted between
  // statuses. renderBody holds the four branch returns exactly as they
  // were ; function declarations hoist, so it is callable here.
  return (
    <>
      {completionAnnouncement}
      {renderBody()}
    </>
  );

  function renderBody() {
    if (status === "working") {
      return (
        <>
          {/*
          OUTSIDE the placeholder, not inside it : the placeholder is
          centered, and .alert sets no text-align, so an Alert nested
          here would render centered while the same Alert renders
          left-aligned in every other state. It also carries
          role="alert", and an assertive live region has no business
          inside the polite role="status" region below, whose one job
          is announcing the finalize call.
        */}
          <ConfigFileSaveError
            error={configSave.error}
            onClose={configSave.clearError}
          />
          {/* role="status" + aria-live (2026-08-14 go-public accessibility
            review) : the automatic finalize call renders no controls
            while it runs, so the announcement is the only cue assistive
            technology gets that Step 4 is doing anything - same posture
            as the page-level restore placeholder (review I10). */}
          <div className="text-center py-8" role="status" aria-live="polite">
            <div className="text-body mb-2">
              Submitting Certificate Signing Request (CSR) and Checking for Your
              Issued SSL Certificate...
            </div>
            <p className="text-muted">Please keep this page open.</p>
          </div>
        </>
      );
    }

    if (status === "processing") {
      return (
        <div className="space-y-4">
          <ConfigFileSaveError
            error={configSave.error}
            onClose={configSave.clearError}
          />
          {/* THE BOX IS FOR THE STATE. THE STEP CARRIES THE CONTENT.
            (operator ruling, 2026-08-23 : "Step 4 can have a blue box but
            the step itself should have the actual content that is deemed
            for step 4".)

            The Alert exists for ONE reason : `live` makes it an ARIA live
            region, so assistive technology announces the change from
            working to signing. That justifies a short status and nothing
            more. It had grown to three paragraphs, two external links and
            a regulatory explanation, all inside a tinted panel - which
            reads as a warning about something having gone wrong when
            nothing has. Same fault as the wildcard note and the retired
            "Domains Already Authorized" Alert : a container chosen for a
            sentence, kept while the content became step material.

            So the state is announced here, and the standing explanation
            below is ordinary step prose. It is true before, during and
            after the wait, which is the test for which side of the line
            it belongs on. */}
          <Alert type="info" title="Signing Your SSL Certificate" live>
            <p>
              Domain Control Validation (DCV) has been successfully completed.
              The Certificate Authority (CA) is now performing further internal
              and regulated checks before signing. If approved, the SSL
              Certificate should be issued within the next few minutes.
            </p>
          </Alert>
          {/* WHAT THE CERTIFICATE AUTHORITY IS ACTUALLY DOING NOW
            (operator ruling, 2026-08-22, written during his own live run).
            A customer told only "this is normal" has no idea what can fail
            here, or that a Domain Name System (DNS) server which cannot be
            reached is enough to stop issuance on its own. Naming the checks
            and pointing at the two reference pages turns a blank wait into
            something they can act on. */}
          <div className="text-body">
            <p>
              Adding Certification Authority Authorization (CAA) records is not
              required, however, the Certificate Authority (CA) is required as
              per the regulations to check if the Domain Name System (DNS) for
              all secured domains contains any and enforce the result. If your
              DNS server cannot be reached or is firewalled geographically the
              issuance process can fail until resolved.
            </p>
            <p className="mt-3">
              These two references explain the checks the Certificate Authority
              (CA) performs :{" "}
              <a
                href="https://shop.trustico.com/pages/caa-records"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                CAA Records
              </a>{" "}
              and{" "}
              <a
                href="https://shop.trustico.com/pages/mpic"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                Multi-Perspective Issuance Corroboration
              </a>
              .
            </p>
          </div>
          {/*
          The signing wait is the other place besides Step 3 where a
          customer legitimately walks away, so it carries the SAME
          step-away control - one component, so the two screens cannot
          drift on the wording, the timeout message or the disclosure
          that has to travel with the file.
        */}
          <StepAwaySave saving={configSave.saving} save={saveThenDisarm} />
          {/*
          Poll-cooldown notice (see pollNoticeAlert above) : the info
          treatment for a Check Progress pressed again within the server's
          cooldown window - the sibling of ChallengePanel's
          "Validation Already Requested" notice.
        */}
          {pollNoticeAlert}
          {/*
          Operator directive 2026-07-05 : the section's centered action
          row. Check Progress stays the primary (the signing wait's expected
          next press) ; Start Over is the outline escape hatch for a
          customer abandoning a stuck order mid-signing. 2026-08-13
          go-public flattening : the pair sits in the equal-width
          two-column grid every other tool page's paired action row
          shares.
        */}
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
              <ActionButton
                onClick={runCheck}
                variant="primary"
                icon={ArrowPathIcon}
                disabled={pollCooldown.isInCooldown()}
              >
                {pollCooldown.isInCooldown()
                  ? cooldownLabel(
                      "Check Progress",
                      pollCooldown.formatTimeRemaining()
                    )
                  : "Check Progress"}
              </ActionButton>
              <StartOverButton onReset={onReset} disarmToken={disarmToken} />
            </div>
          </div>
        </div>
      );
    }

    if (status === "failed") {
      return (
        <div className="space-y-4">
          <ConfigFileSaveError
            error={configSave.error}
            onClose={configSave.clearError}
          />
          <Alert type="error" title="SSL Certificate Issuance Failed" live>
            {error}
          </Alert>
          {/*
          Poll-cooldown notice on the RESTORED failed screen
          (2026-08-14 go-public review) : without it, a Retry pressed
          inside the cooldown window appeared to do nothing at all -
          the restored banner alone gave no clue the press was paced,
          not ignored.
        */}
          {pollNoticeAlert}
          {/*
          Operator directive 2026-07-05 : the section's centered action
          row. Retry stays the primary recovery ; Start Over is the
          outline exit for an order that will never recover (the
          dead-order class the ChallengePanel stalled-order guidance
          documents). 2026-08-13 go-public flattening : the equal-width
          two-column grid idiom, as above.
        */}
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
              {/* THE SAME WINDOW AS CHECK PROGRESS, because it is the same
                call : Retry re-runs runCheck, and finalizeAndIssue
                meters both presses against lastFinalizePollAt. Two
                render sites of one control, so both carry the
                countdown - leaving this one bare would put the
                original fault back on the screen a customer reaches
                only after something has already gone wrong. */}
              <ActionButton
                onClick={runCheck}
                variant="primary"
                icon={ArrowPathIcon}
                disabled={pollCooldown.isInCooldown()}
              >
                {pollCooldown.isInCooldown()
                  ? cooldownLabel("Retry", pollCooldown.formatTimeRemaining())
                  : "Retry"}
              </ActionButton>
              <StartOverButton onReset={onReset} disarmToken={disarmToken} />
            </div>
          </div>
        </div>
      );
    }

    if (!bundle) return null;

    /**
     * How many intermediate SSL Certificates the chain carries.
     *
     * The tracking system's heading reads "Intermediate SSL Certificates
     * (2)" and the operator asked for the same treatment here
     * (2026-08-23). COUNTED from the PEM rather than hard-coded : the
     * number of intermediates differs by Certificate Authority product and
     * changes when a CA rolls its issuing chain, so a literal would be
     * wrong the first time that happens and nothing would notice.
     *
     * Counts BEGIN markers rather than splitting on END, so a trailing
     * newline or a blank line between blocks cannot change the answer.
     */
    const intermediateCount = (
      String(bundle.intermediates || "").match(
        /-----BEGIN CERTIFICATE-----/g
      ) || []
    ).length;

    // 2026-07-04 operator consistency directive ("We require explanatory
    // text under EVERY menu option") : each artifact card's heading now
    // carries a one-sentence description, matching the
    // heading-then-paragraph idiom of the reference tool pages (the
    // "Certificate Signing Request (CSR)" result card in
    // src/client/pages/CSRGenerator.jsx). The description parameter sits
    // right after label so every call site reads heading-then-paragraph
    // in source order too.
    const artifact = (label, description, value, downloadType, fieldKey) => {
      // 2026-05-31 seventh-round code review nit-5 : the textarea
      // is decorative ; its content is the artifact value. Wire
      // aria-labelledby to the heading so assistive technology
      // reads "<heading> textarea" instead of just "textarea".
      // The id is derived from fieldKey which is already unique
      // per artifact card.
      const headingId = "issuance-artifact-heading-" + fieldKey;
      return (
        /*
        2026-08-13 go-public flattening (operator-directed : "avoiding
        boxes in boxes") : each artifact previously drew its own
        bordered box filled byte-identical to the StepPanel .card, with
        the bordered textarea inside it - three same-coloured surfaces
        separated by hairlines. The wrapper frame is dissolved (the
        heading, description, textarea and compact utility row already
        read as one unit, the CSRGenerator result idiom) and the
        textarea takes the shared certificate-textarea treatment every
        other PEM artifact on the site uses, whose recessed bg-gray-50
        fill doubles as the value's visual anchor.
      */
        /*
        Flat block, NOT a nested card : this step's content already
        renders inside StepPanel's `card p-6`, and wrapping each
        artifact in a second card is the boxes-in-boxes shape the
        operator rejected on 2026-08-13. The heading, description,
        recessed textarea and centered button row read as one unit on
        their own, exactly as the result blocks do on
        src/client/pages/CSRGenerator.jsx - the difference there is
        only that those blocks sit at page level, where the card frame
        is the outermost surface rather than a second one.

        Spacing is owned by the parent's space-y-6, so no artifact
        carries its own margin and every gap on the step is equal.
      */
        <div>
          <h3
            id={headingId}
            className="text-lg font-semibold text-heading mb-2"
          >
            {label}
          </h3>
          <p className="text-body mb-4">{description}</p>
          <textarea
            aria-labelledby={headingId}
            value={value}
            readOnly
            rows={12}
            className="textarea certificate-textarea mb-4"
          />
          {/*
          The site's result-artifact action row, byte-for-byte the
          idiom every sibling result card uses (see the CSR and
          Private Key cards in src/client/pages/CSRGenerator.jsx) :
          two equal-width outline ActionButtons in a centered
          max-w-2xl grid.

          These were previously hand-rolled <button> elements with
          ad-hoc padding and border classes - the only controls on the
          site not built from the shared button components, so they
          rendered at a different height and weight from every other
          Copy and Download control a customer had already used.
        */}
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
              {/*
              aria-label (2026-08-13 go-public cleanup, audit :
              "accessibility") : all four artifact Copy buttons render
              the same visible text, so without a label a screen-reader
              user cannot tell the private key copy control from the
              three SSL Certificate ones. The label interpolates
              the artifact name and flips with the visible state - the
              same contract src/client/components/CopyValueButton.jsx
              documents for the compact field copies.
            */}
              <ActionButton
                variant="outline"
                icon={ClipboardDocumentIcon}
                onClick={() => {
                  // Disarm the two-press reset guard (2026-08-14
                  // go-public review sweep) : the SAME changed-intent
                  // rule the ZIP / configuration file handlers apply - a
                  // customer who armed the terminal reset and then went
                  // off to copy an artifact is no longer confirming, and
                  // an armed guard here left them one click from the
                  // destructive reset.
                  disarmReset();
                  handleCopy(value, fieldKey);
                }}
                aria-label={
                  copyStatus[fieldKey] === false
                    ? `Copy Failed - Select the ${label} Manually`
                    : copyStatus[fieldKey]
                      ? // The artifact name stays in EVERY state
                        // (2026-08-14 go-public review) : four identical
                        // copy controls sit on this step, one of them the
                        // private key, and a bare "Copied!" announcement
                        // discards the distinction the label exists for.
                        `${label} Copied`
                      : `Copy ${label}`
                }
              >
                {/* Short visible label with the artifact name carried
                  by the aria-label above : the heading naming this
                  artifact sits directly over the button, and the full
                  name inside the control wrapped onto two lines at
                  this column width. Same compact idiom as the copy
                  controls on Step 3. */}
                {copyStatus[fieldKey] === false
                  ? "Copy Failed"
                  : copyStatus[fieldKey]
                    ? "Copied!"
                    : "Copy"}
              </ActionButton>
              {/*
              aria-label (2026-08-14 go-public review) : the sibling of
              the Copy control's contract above - without the
              interpolated artifact name a screen-reader user cannot
              tell the private key download from the three SSL
              Certificate ones.
            */}
              <ActionButton
                variant="outline"
                icon={ArrowDownTrayIcon}
                onClick={() => {
                  // Same changed-intent disarm as the Copy control above.
                  disarmReset();
                  downloadFile(
                    value,
                    fileFor(downloadType),
                    // PEM regardless of the .crt extension on the
                    // leaf : tracking-01 writes PEM into its own
                    // `-certificate.crt` too.
                    "application/x-pem-file"
                  );
                }}
                aria-label={`Download ${label}`}
              >
                Download
              </ActionButton>
            </div>
          </div>
        </div>
      );
    };

    return (
      /*
      space-y-6 owns EVERY vertical gap on this step
      (operator directive 2026-08-15 : spacing must be the same
      between elements). The blocks below previously set their own
      mt-3, mt-4, mt-6 and mb-6 margins, so the gaps between the
      blocks - the four artifacts, the download row, the guidance and
      the final action - were four different sizes.
    */
      <div className="space-y-6">
        <ConfigFileSaveError
          error={configSave.error}
          onClose={configSave.clearError}
        />
        {/*
        2026-07-03 adversarial review finding : the error state used to
        render ONLY inside the status === "failed" early return, so a
        Configuration File or ZIP download failure in THIS (complete)
        state called setError invisibly - the customer left believing
        they had saved credentials they never received. The Valkey
        migration widened that path : /configuration now 503s while the
        store is unavailable (the deliberate fail-closed posture) and
        404s once the 48-hour TTL lapses under an open Step 4 tab, and
        the Configuration File is the only post-issuance resume
        artifact. Render the error here too, dismissably.
      */}
        {error && (
          <Alert
            type="error"
            title="Download Failed"
            live
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* THE SAME WORDS THE TRACKING SYSTEM USES (operator ruling,
          2026-08-23 : "Our tracking system has this text in various places
          and you have not used the same type ... I am asking you to be
          consistent with what we offer"). The section lead, the two SSL
          Certificate headings and their descriptions are the tracking
          system's, so a customer who has downloaded from there reads the
          same explanation here. What differs is only the list of formats,
          because it names what THIS tool actually produces rather than
          what that page offers. */}
        <p className="text-body">
          The SSL Certificate content below has been retrieved from the
          Certificate Authority (CA) and includes the complete SSL Certificate
          chain including your end-entity SSL Certificate and all intermediate
          SSL Certificates. You can copy individual SSL Certificates or download
          them in various formats using the options provided.
        </p>
        {artifact(
          "Your SSL Certificate",
          "This is the SSL Certificate issued for your fully qualified domain name. Copy this SSL Certificate to install on your web server, hosting account, or any other service compatible with an SSL Certificate. Sometimes this is called the leaf or end-entity SSL Certificate.",
          bundle.leaf,
          ARCHIVE_MEMBERS.leaf,
          "leaf"
        )}
        {bundle.intermediates &&
          artifact(
            `Intermediate SSL Certificates (${intermediateCount})`,
            "These are the intermediate SSL Certificates in the trust chain. Install these alongside your SSL Certificate so browsers can verify the complete chain of trust and ensure ubiquity across all browsers. Sometimes these are also called a bundle or chain.",
            bundle.intermediates,
            ARCHIVE_MEMBERS.intermediates,
            "chain"
          )}
        {/* The tracking system's register for these two as well
          (2026-08-23) : identify the artifact, say what to do with it,
          then give the other names people call it - the shape both of
          his examples follow.

          "SSL Certificate with Chain" rather than "Full Chain" so the
          section and the Download Options sentence use the same words ;
          the jargon survives in the last line for anyone who knows it.
          "Your Private Key" rather than "SSL Certificate Key File" so it
          pairs visibly with "Your SSL Certificate" above - those two are
          the matched pair, and the old name read like a fourth kind of
          certificate.

          The private key description runs to four sentences where the
          others take three, because it is the ONLY artifact on this page
          that cannot be recovered : everything else is re-fetchable from
          the Certificate Authority with the account key and the order
          URL. That warning cannot live only in the security notice at
          the top of the page, which is read once on arrival, when this
          is read at the moment the customer decides what to save. */}
        {artifact(
          "SSL Certificate with Chain",
          "This is your SSL Certificate and the intermediate SSL Certificates combined into a single file. Many server configurations ask for the SSL Certificate and its chain together rather than as separate files, and this is the file to use where that is the case. Sometimes this is called the full chain.",
          bundle.fullChain,
          ARCHIVE_MEMBERS.chain,
          "fullchain"
        )}
        {artifact(
          "Your Private Key",
          // "CANNOT BE RECOVERED" WAS FALSE (2026-08-23 review of PR #107).
          // buildCertificateBundleView returns certificatePrivateKey on every
          // finalize and /configuration echoes it, so for the session's
          // life the key comes back on a reload and inside the
          // Configuration File. StepAwaySave says so, and this
          // sentence's own interpolated window says so - the claim
          // contradicted both. (The page-level Security Notice used to be
          // the evidence cited here ; it became the site-wide standard
          // text on 2026-08-23 and no longer describes retention.) It is
          // also the exact
          // phrasing the
          // 2026-08-14 honest-posture review removed from this screen,
          // because it pushed customers whose browser crashed before
          // downloading into needless second orders when a reload would
          // have restored everything. The true and still-urgent statement
          // is that it goes when the session does, and cannot be reissued.
          `This is the private key generated for your SSL Certificate. Install it on the same server as your SSL Certificate, as the two only work as a matched pair and an SSL Certificate cannot be used without it. Download and store it securely now, because it is destroyed along with the issuance request after ${SESSION_LIFETIME_HOURS} hours and cannot be reissued. Sometimes this is called the key file or the server key.`,
          bundle.certificatePrivateKey,
          ARCHIVE_MEMBERS.privateKey,
          "privatekey"
        )}

        {/* DOWNLOAD OPTIONS, in the tracking system's shape but naming
          what THIS tool produces. That page offers six formats including
          PKCS#7 and a chain-plus-root file ; this one writes the four
          files above, a ZIP of all of them, and the Trustico®
          configuration file. Describing its list here would promise
          formats we do not generate. */}
        <div>
          <h3 className="text-lg font-semibold text-heading mb-2">
            Download Options
          </h3>
          <p className="text-body">
            Choose the download format that best suits your server
            configuration. Each file above can be downloaded on its own : the
            SSL Certificate, the intermediate SSL Certificates for server
            configurations that use separate files, the SSL Certificate combined
            with its chain, and the private key. The ZIP archive bundles every
            file together for storage, and the configuration file lets you
            return to this issuance request later.
          </p>
        </div>

        {/* 2026-08-13 go-public flattening : the equal-width two-column
          grid idiom every other tool page's paired action row shares. */}
        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            <ActionButton
              onClick={handleDownloadZip}
              variant="primary"
              icon={ArchiveBoxIcon}
            >
              Download ZIP Archive
            </ActionButton>
            <ActionButton
              onClick={handleDownloadConfigFile}
              variant="outline"
              icon={ArrowDownOnSquareIcon}
              loading={downloadingConfig}
              loadingText="Preparing Configuration File..."
            >
              Download Configuration
            </ActionButton>
          </div>
        </div>
        {/*
        Operator directive 2026-07-05 : Step 4 is the flow's TERMINAL
        step, so its action row carries NO Continue - the new-order
        action is PRIMARY here. It sits below the downloads and the
        guidance so the customer saves everything first, and it reuses
        the page-level reset handler unchanged
        (local reset first, fire-and-forget discard).

        2026-08-13 operator ruling (go-public cleanup) : renamed from
        "Start Over" to name what it actually does, with an inline
        two-press confirmation - one press used
        to discard the server session and unmount this step,
        permanently destroying the just-issued Private Key with no
        warning (the most destructive click on the site ; the key can
        never be re-fetched). Earlier steps keep the "Start Over"
        label : there it abandons an incomplete order, which is cheap.
      */}
        {/* Next Steps, titled "What to Do Next" until 2026-08-23 when
            the operator held it against the two-and-three-letter rule
            ("Do") (2026-08-13 operator content ruling) : the
          customer was handed four files and told what each IS but
          never what to DO with them. The install-page URL is
          operator-supplied (2026-08-13) - never change it without the
          operator's word. */}
        {/* Heading and prose tiers are the site canon (text-lg
          semibold over base-size prose), the same pair every sibling
          tool card uses - see the result cards in
          src/client/pages/CSRGenerator.jsx. Flat, for the same
          no-nested-cards reason as the artifact blocks above. */}
        <div>
          <h3 className="text-lg font-semibold text-heading mb-2">
            Next Steps
          </h3>
          <p className="text-body mb-3">
            Most web servers need two of these files - the SSL Certificate with
            chain, and your private key. Upload both to your server, point your
            server configuration at them, then restart the server so the new SSL
            Certificate is served. Step-by-step instructions for common servers
            are on the{" "}
            <a
              href="https://shop.trustico.com/pages/install"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link underline"
            >
              Trustico® SSL Certificate Installation
            </a>{" "}
            page.
          </p>
          {/*
          The no-automatic-renewal consequence (operator content ruling
          2026-08-13, previously the closing sentence of the page
          introduction). It moved here in the 2026-08-15 consistency
          pass because the page introduction had to become the single
          short descriptive line every sibling tool carries. No
          cross-tool redirect, per the same ruling.
        */}
          {/* TWO PARAGRAPHS, NOT ONE (operator, 2026-08-23 : "that
              paragraph you wrote is too long and would need to be split
              in two"). They are two subjects : when to come back, and
              what the configuration file is and why it has to be
              guarded. The gap matches the one above it AND the rest of
              the flow.
              Both are mt-3 / mb-3 : twelve pixels is the
              paragraph-to-paragraph tier every stacked paragraph in the
              issuance components uses. They were briefly 16px, matching
              each other but nothing else - which satisfied the local
              reading of "spacing must be the same between elements" and
              failed the whole-flow one the operator meant on
              2026-08-23. Twelve pixels between stacked paragraphs is a
              tier of its own in docs/UI_STANDARD.md ("Vertical spacing
              has fixed tiers"), which lists the rest. */}
          <p className="text-body">
            This tool issues one configured SSL Certificate at a time and does
            not renew it automatically. Please make a note of when your SSL
            Certificate expires by viewing the validity dates once installed.
            Return here to issue a replacement SSL Certificate whilst your
            license is valid.
          </p>
          <p className="text-body mt-3">
            Download the configuration file and keep it safe as it contains data
            related to this issuance request. The configuration file also
            contains a backup of the issued SSL Certificate, intermediates and
            key. The configuration file allows the ACME account to be restored
            and streamlines the reissue process shall you need to obtain a newly
            reissued SSL Certificate at any time.
          </p>
        </div>

        <div className="flex justify-center">
          <StartOverButton
            onReset={onReset}
            label="Issue Another SSL Certificate"
            confirmLabel="Press Again to Confirm - This Clears the SSL Certificate"
            armedAnnouncement="Issue Another SSL Certificate again to start a new issuance request. This clears the issued SSL Certificate and its files from this page and they cannot be recovered."
            variant="primary"
            disarmToken={disarmToken}
          />
        </div>
      </div>
    );
  }
}

export default Step4Download;
